import React, { useEffect, useState } from "react";
import Form from "react-bootstrap/Form";

import DstButton from "../../../../components/DstButton/DstButton";
import MultiSelectDropdown from "../../../../components/MultiSelectDropdown/MultiSelectDropdown";
import MultiSelectLiveDropdown from "../../../../components/MultiSelectLiveDropdown/MultiSelectLiveDropdown";
import RequiredLabel from "../../../../components/RequiredLabel/RequiredLabel";
import { useSession } from "../../../../contexts/SessionContext";
import { LanguageEnum } from "../../../../models/Language";
import { ProgrammingLanguageEnum } from "../../../../models/ProgrammingLanguage";
import { PurposeEnum } from "../../../../models/Purpose";
import translate from "../../../../services/Translate";
import { createModule, fetchPrograms } from "./CreateModuleForm.function";

const CreateModuleForm = ({ onSuccess, onCancel }: { onSuccess: Function; onCancel: Function }) => {
  const { language, showAlert, popAlert } = useSession();

  const [loading, setLoading] = useState(false);
  const [programs, setPrograms] = useState<any[]>([]);
  const [loadingPrograms, setLoadingPrograms] = useState(true);
  const [owner, setOwner] = useState<any | null>(null);

  const [form, setForm] = useState({
    name: "",
    description: "",
    programming_language: "",
    owner_id: "",
    language: "",
    purpose: "",
    origin_program_id: "",
    is_deprecated: false,
    image_url: "",
  });

  const _handleChange = (key: string, value: any) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const isValid =
    !loadingPrograms &&
    form.name &&
    form.description &&
    form.programming_language &&
    owner &&
    form.language &&
    form.origin_program_id &&
    form.purpose;

  const _handleSubmit = () => {
    if (!isValid) return;

    createModule(
      language,
      {
        name: form.name,
        description: form.description,
        programming_language: form.programming_language,
        owner_id: owner?.id,
        language: form.language,
        origin_program_id: Number(form.origin_program_id),
        purpose: form.purpose,
        is_deprecated: form.is_deprecated,
        image_url: form.image_url || undefined,
      },
      setLoading,
      onSuccess,
      showAlert,
      popAlert
    );
  };

  useEffect(() => {
    fetchPrograms(language, setPrograms, setLoadingPrograms);
  }, [language]);

  return (
    <Form className="p-3">
      <Form.Group className="mb-3">
        <Form.Label>
          <RequiredLabel label={translate(language, "PAGES.MODULES.CREATE.NAME")} />
        </Form.Label>
        <Form.Control value={form.name} onChange={(event) => _handleChange("name", event.target.value)} />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>
          <RequiredLabel label={translate(language, "GLOBAL.DESCRIPTION")} />
        </Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          value={form.description}
          onChange={(event) => _handleChange("description", event.target.value)}
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>
          <RequiredLabel label={translate(language, "PAGES.MODULES.CREATE.PROGRAMMING_LANGUAGE")} />
        </Form.Label>
        <Form.Select
          value={form.programming_language}
          onChange={(event) => _handleChange("programming_language", event.target.value)}
        >
          <option value="" disabled>
            {translate(language, "PAGES.MODULES.CREATE.SELECT_LANGUAGE")}
          </option>

          {Object.values(ProgrammingLanguageEnum).map((lang) => (
            <option key={lang} value={lang}>
              {lang}
            </option>
          ))}
        </Form.Select>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>
          <RequiredLabel label={translate(language, "PAGES.MODULES.CREATE.OWNER")} />
        </Form.Label>
        <MultiSelectLiveDropdown
          category={["users"]}
          language={language}
          isSingleSelect
          label={
            owner ? `${owner.first_name} ${owner.last_name}` : translate(language, "PAGES.MODULES.CREATE.SELECT_OWNER")
          }
          toggleClass="w-100 border"
          onConfirm={(selectedUsers: any[]) => setOwner(selectedUsers.length ? selectedUsers[0].value : null)}
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>
          <RequiredLabel label={translate(language, "GLOBAL.LANGUAGE")} />
        </Form.Label>

        <Form.Select value={form.language} onChange={(event) => _handleChange("language", event.target.value)}>
          <option value="" disabled>
            {translate(language, "PAGES.MODULES.CREATE.SELECT_LANGUAGE")}
          </option>

          {Object.values(LanguageEnum).map((lang) => (
            <option key={lang} value={lang}>
              {lang}
            </option>
          ))}
        </Form.Select>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>
          <RequiredLabel label={translate(language, "PAGES.MODULES.CREATE.ORIGIN_PROGRAM")} />
        </Form.Label>

        <MultiSelectDropdown
          btnWidth="100%"
          items={programs}
          language={language}
          isSingleSelect
          badgeItemProperty="name"
          label={
            form.origin_program_id
              ? programs.find((program) => program.id === Number(form.origin_program_id))?.name
              : translate(language, "PAGES.MODULES.CREATE.SELECT_PROGRAM")
          }
          displayItem={(program) => program.name}
          onConfirm={(selectedItems) => {
            _handleChange("origin_program_id", selectedItems.length ? selectedItems[0].id : "");
          }}
          mock={loadingPrograms}
          toggleClass="w-100"
          borderToggle
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>
          <RequiredLabel label={translate(language, "PAGES.MODULES.CREATE.PURPOSE")} />
        </Form.Label>
        <Form.Select value={form.purpose} onChange={(event) => _handleChange("purpose", event.target.value)}>
          <option value="" disabled>
            {translate(language, "PAGES.MODULES.CREATE.SELECT_PURPOSE")}
          </option>

          {Object.values(PurposeEnum).map((purpose) => (
            <option key={purpose} value={purpose}>
              {purpose}
            </option>
          ))}
        </Form.Select>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Check
          type="switch"
          label={translate(language, "PAGES.MODULES.CREATE.DEPRECATED")}
          checked={form.is_deprecated}
          onChange={(event) => _handleChange("is_deprecated", event.target.checked)}
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>{translate(language, "PAGES.MODULES.CREATE.IMAGE_URL")}</Form.Label>
        <Form.Control value={form.image_url} onChange={(event) => _handleChange("image_url", event.target.value)} />
      </Form.Group>

      <div className="d-flex justify-content-end">
        <DstButton
          value={translate(language, "GLOBAL.CANCEL")}
          variant="light"
          btnClass="me-2"
          clickFunction={onCancel}
        />

        <DstButton
          value={translate(language, "GLOBAL.CREATE")}
          variant="primary"
          loading={loading}
          disabled={!isValid}
          clickFunction={_handleSubmit}
        />
      </div>
    </Form>
  );
};

export default CreateModuleForm;
